/**
 * Admin page for announcements. An admin writes a title and a text, chooses who it is for, and
 * publishes it. It then shows among the notifications of everyone, of brands or of creators. An
 * announcement can be taken back, which hides it from its readers and keeps it on this list.
 */

import { Megaphone } from "lucide-react";
import { type FormEvent, useId, useState } from "react";

import {
  ANNOUNCEMENT_LIMITS,
  type Announcement,
  type AnnouncementAudience,
  useAnnouncements,
  usePublishAnnouncement,
  useWithdrawAnnouncement,
} from "../../admin/announcements.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { Select, type SelectOption } from "../../components/forms.js";
import { RequireAdmin } from "../../components/RequireAdmin.js";
import {
  Badge,
  EmptyState,
  ErrorMessage,
  Loading,
  Notice,
  PageHeader,
  styles,
} from "../../components/ui.js";
import { formatUtc } from "../../format.js";

export function AdminAnnouncements() {
  return (
    <RequireAdmin>
      <AnnouncementsPage />
    </RequireAdmin>
  );
}

const AUDIENCES: SelectOption<AnnouncementAudience>[] = [
  { value: "everyone", label: "Everyone", hint: "Every wallet that has signed in" },
  { value: "brands", label: "Brands", hint: "Wallets that sent a campaign request" },
  { value: "creators", label: "Creators", hint: "Wallets with a linked account" },
];
const AUDIENCE_NAMES: Record<AnnouncementAudience, string> = {
  everyone: "Everyone",
  brands: "Brands",
  creators: "Creators",
};

/** A link an announcement may carry: a page of the app, or an https address. */
const LINK_PATTERN = /^(\/(?!\/)|https:\/\/)\S*$/;

function AnnouncementsPage() {
  const list = useAnnouncements();
  return (
    <section>
      <PageHeader eyebrow="Admin" title="Announcements" documentTitle="Announcements · Admin">
        What Morcast tells its users, such as an update or a notice. An announcement shows among the
        notifications of the wallets it is for, from the moment you publish it.
      </PageHeader>

      <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <PublishForm />
        <section aria-labelledby="published-title">
          <h2 id="published-title" className="text-xl font-semibold tracking-tight">
            Published
          </h2>
          <div className="mt-4">
            {list.isPending && <Loading />}
            {list.isError && <ErrorMessage error={list.error} />}
            {list.data?.length === 0 && (
              <EmptyState icon={Megaphone} title="No announcement yet">
                The first one you publish appears here.
              </EmptyState>
            )}
            {list.data && list.data.length > 0 && (
              <ul aria-label="Announcements" className="space-y-3">
                {list.data.map((announcement) => (
                  <AnnouncementCard key={announcement.id} announcement={announcement} />
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </section>
  );
}

function PublishForm() {
  const publish = usePublishAnnouncement();
  const fieldId = useId();
  const [audience, setAudience] = useState<AnnouncementAudience>("everyone");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [confirming, setConfirming] = useState(false);

  const problems = [
    title.trim().length < ANNOUNCEMENT_LIMITS.title[0] ? "Give it a title." : null,
    body.trim().length < ANNOUNCEMENT_LIMITS.body[0]
      ? "Write the text, in at least one full sentence."
      : null,
    link.trim() !== "" && !LINK_PATTERN.test(link.trim())
      ? "The link must be a page of Morcast, such as /docs, or an address that starts with https://."
      : null,
  ].filter((problem) => problem !== null);
  const [shown, setShown] = useState(false);

  function submit(event: FormEvent) {
    event.preventDefault();
    setShown(true);
    if (problems.length === 0) {
      setConfirming(true);
    }
  }

  function send() {
    publish.mutate(
      {
        audience,
        title: title.trim(),
        body: body.trim(),
        ...(link.trim() ? { link: link.trim() } : {}),
      },
      {
        onSuccess: () => {
          setTitle("");
          setBody("");
          setLink("");
          setShown(false);
        },
      },
    );
  }

  return (
    <form onSubmit={submit} aria-labelledby={`${fieldId}-form`} className={styles.card} noValidate>
      <h2 id={`${fieldId}-form`} className="font-semibold">
        Write an announcement
      </h2>
      <div className="mt-4 space-y-4 text-sm">
        <div>
          <label
            id={`${fieldId}-audience-label`}
            htmlFor={`${fieldId}-audience`}
            className="font-medium"
          >
            For
          </label>
          <Select
            id={`${fieldId}-audience`}
            labelId={`${fieldId}-audience-label`}
            value={audience}
            options={AUDIENCES}
            onChange={setAudience}
          />
        </div>
        <label className="block font-medium">
          Title
          <input
            type="text"
            value={title}
            maxLength={ANNOUNCEMENT_LIMITS.title[1]}
            onChange={(event) => setTitle(event.target.value)}
            className={styles.input}
          />
        </label>
        <label className="block font-medium">
          Text
          <textarea
            rows={4}
            value={body}
            maxLength={ANNOUNCEMENT_LIMITS.body[1]}
            onChange={(event) => setBody(event.target.value)}
            className={styles.input}
          />
          <span className="mt-1 block text-xs font-normal text-zinc-500 dark:text-zinc-400">
            {body.length} of {ANNOUNCEMENT_LIMITS.body[1]} characters
          </span>
        </label>
        <label className="block font-medium">
          Link, if it leads somewhere
          <input
            type="text"
            value={link}
            maxLength={ANNOUNCEMENT_LIMITS.link}
            placeholder="/docs or https://…"
            onChange={(event) => setLink(event.target.value)}
            className={styles.input}
          />
        </label>
      </div>
      {shown && problems.length > 0 && (
        <Notice tone="warning" className="mt-4">
          <ul className="space-y-1">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </Notice>
      )}
      {publish.isError && (
        <div className="mt-4">
          <ErrorMessage error={publish.error} />
        </div>
      )}
      {publish.isSuccess && !shown && (
        <Notice tone="success" className="mt-4">
          Published. Its readers see it among their notifications.
        </Notice>
      )}
      <button type="submit" disabled={publish.isPending} className={`mt-5 ${styles.primaryButton}`}>
        {publish.isPending ? "Publishing…" : "Publish"}
      </button>
      <ConfirmDialog
        open={confirming}
        title="Publish this announcement?"
        confirmLabel="Publish"
        cancelLabel="Keep editing"
        onConfirm={send}
        onClose={() => setConfirming(false)}
      >
        <p>
          {AUDIENCE_NAMES[audience]} will see "{title.trim()}" among their notifications right away.
          You can take it back later, which hides it again.
        </p>
      </ConfirmDialog>
    </form>
  );
}

function AnnouncementCard({ announcement }: { announcement: Announcement }) {
  const withdraw = useWithdrawAnnouncement();
  const [confirming, setConfirming] = useState(false);
  const withdrawn = announcement.withdrawnAt !== null;
  return (
    <li className={styles.card}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="grey">{`For ${AUDIENCE_NAMES[announcement.audience].toLowerCase()}`}</Badge>
        {withdrawn ? <Badge tone="amber">Taken back</Badge> : <Badge tone="green">Shown</Badge>}
      </div>
      <h3 className={`mt-3 font-semibold ${withdrawn ? "text-zinc-500 dark:text-zinc-400" : ""}`}>
        {announcement.title}
      </h3>
      <p className="mt-1 text-sm whitespace-pre-line text-zinc-600 dark:text-zinc-400">
        {announcement.body}
      </p>
      {announcement.link && (
        <p className="mt-2 text-sm break-all text-zinc-600 dark:text-zinc-400">
          Leads to <span className="font-mono text-xs">{announcement.link}</span>
        </p>
      )}
      <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
        Published {formatUtc(announcement.createdAt)}
        {announcement.withdrawnAt && `. Taken back ${formatUtc(announcement.withdrawnAt)}`}
      </p>
      {!withdrawn && (
        <button
          type="button"
          disabled={withdraw.isPending}
          onClick={() => setConfirming(true)}
          className={`mt-4 ${styles.secondaryButtonSmall}`}
        >
          {withdraw.isPending ? "Taking back…" : "Take back"}
        </button>
      )}
      {withdraw.isError && (
        <div className="mt-3">
          <ErrorMessage error={withdraw.error} />
        </div>
      )}
      <ConfirmDialog
        open={confirming}
        title="Take this announcement back?"
        confirmLabel="Take back"
        cancelLabel="Keep it shown"
        onConfirm={() => withdraw.mutate(announcement.id)}
        onClose={() => setConfirming(false)}
      >
        <p>
          "{announcement.title}" disappears from its readers' notifications. It stays on this list.
        </p>
      </ConfirmDialog>
    </li>
  );
}
